import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Apple, Smartphone, Tablet, ShieldCheck, Zap, Rocket, Star, Info, Download, LayoutGrid, ListChecks } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/tutoriais-iptv/iphone")({
  head: () => ({
    meta: [
      { title: "Como Instalar IPTV no iPhone: Guia Completo iOS 2026" },
      { name: "description", content: "Aprenda como configurar IPTV no seu iPhone ou iPad. Guia completo com apps oficiais da App Store como Smarters Lite, Vizzion e IBO Pro para Painel IPTV Pós Pago." },
    ],
  }),
  component: IPhoneTutorial,
});

function IPhoneTutorial() {
  const apps = [
    { name: "Smarters Player Lite", type: "Popular", desc: "A escolha número 1 dos usuários de iOS pela sua interface amigável e facilidade extrema de configuração." },
    { name: "Vizzion", type: "Moderno", desc: "Aplicativo parceiro com visual futurista e navegação fluida, otimizado para os iPhones mais recentes." },
    { name: "IBO Pro Player", type: "Estável", desc: "Focado em performance e estabilidade, ideal para quem busca uma reprodução de conteúdo sem engasgos." },
    { name: "IPTV Player.io", type: "Prático", desc: "Interface minimalista e direta, perfeita para quem quer praticidade no dia a dia." }
  ];

  const devices = [
    { name: "iPhone", icon: <Smartphone className="w-5 h-5" /> },
    { name: "iPad", icon: <Tablet className="w-5 h-5" /> },
    { name: "Apple TV", icon: <LayoutGrid className="w-5 h-5" /> },
    { name: "MacBook (M1/M2)", icon: <Smartphone className="w-5 h-5" /> }
  ];

  return (
    <SEOPage
      title="Como Instalar IPTV no iPhone: Guia Completo iOS 2026"
      subtitle="O entretenimento premium da Apple encontra a melhor estrutura de IPTV do mercado."
      category="Tutoriais iOS"
      introParagraphs={[
        "O iPhone é reconhecido mundialmente pelo seu desempenho e qualidade de tela inigualáveis, o que o torna a plataforma mobile perfeita para assistir conteúdos via IPTV. Através da App Store, você tem acesso a players seguros e de alta performance.",
        "Nossa infraestrutura de Painel IPTV Pós Pago é totalmente compatível com o ecossistema iOS, garantindo que você tenha a mesma estabilidade e rapidez de uma Smart TV na palma da sua mão."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "iPhone", href: "/tutoriais-iptv/iphone" }
      ]}
      benefits={[
        { title: "Segurança Apple", description: "Apps verificados e seguros baixados diretamente da App Store oficial." },
        { title: "Performance Fluida", description: "Aproveite o poder do hardware da Apple para streamings em 4K." },
        { title: "Mobilidade Total", description: "Sua programação favorita acompanha você em qualquer lugar." },
        { title: "Interface Intuitiva", description: "Navegação nativa e gestos simples para trocar de canais." },
        { title: "Gestão Pós-Paga", description: "O modelo de revenda mais moderno e lucrativo para iPhone." },
        { title: "Sincronização Cloud", description: "Seus dados de acesso sincronizados entre todos seus dispositivos Apple." }
      ]}
      howItWorks={[
        { title: "App Store", description: "Abra a App Store oficial no seu iPhone ou iPad." },
        { title: "Pesquisa", description: "Busque por 'Smarters Lite' ou outro app homologado." },
        { title: "Download", description: "Toque em 'Obter' e aguarde a instalação automática." },
        { title: "Conexão", description: "Abra o app, insira seus dados e comece a assistir." }
      ]}
      diferenciais={[
        { title: "Risco Zero", description: "No modelo pós-pago, você não precisa empatar capital em estoque de créditos." },
        { title: "Servidores P2PRO", description: "Tecnologia de ponta que garante 99.9% de disponibilidade de sinal." },
        { title: "Ativação Instantânea", description: "Configure novos clientes pelo celular em poucos segundos." },
        { title: "Suporte VIP", description: "Consultoria especializada para você escalar sua revenda profissional." }
      ]}
      faq={[
        { question: "Posso usar IPTV em qualquer iPhone?", answer: "Sim, qualquer iPhone que suporte as versões atuais do iOS permite a instalação dos aplicativos oficiais da App Store." },
        { question: "Preciso baixar arquivos externos?", answer: "Não. Por segurança, recomendamos apenas aplicativos disponíveis oficialmente na App Store da Apple." },
        { question: "Qual o melhor app IPTV para iPhone?", answer: "O Smarters Player Lite e o Vizzion são os mais recomendados pela facilidade de uso no iOS." },
        { question: "Como funciona o teste para iPhone?", answer: "Fale com nossos consultores agora mesmo e receba um acesso de teste para validar a qualidade no seu dispositivo." },
        { question: "O sinal trava no iPhone?", answer: "Com nossa rede de servidores premium e apps homologados, você terá uma experiência estável até em redes móveis (4G/5G)." }
      ]}
      sections={[
        {
          title: "Compatibilidade Apple",
          content: (
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {devices.map((device, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-bold uppercase tracking-widest">
                  {device.icon}
                  {device.name}
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Aplicativos Recomendados para iOS",
          type: "grid",
          content: (
            <>
              {apps.map((app, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-electric transition-colors">{app.name}</h3>
                    <span className="text-[10px] font-black uppercase tracking-widest bg-electric/10 text-electric px-2 py-1 rounded-md">{app.type}</span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{app.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "A Revolução do Painel Pós Pago P2PRO",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-electric/20 p-3 rounded-2xl text-electric shrink-0">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Escalabilidade Imediata</h4>
                    <p className="text-muted-foreground text-sm">Gerencie centenas de acessos mobile com total controle operacional e financeiro.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-electric/20 p-3 rounded-2xl text-electric shrink-0">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Qualidade Apple</h4>
                    <p className="text-muted-foreground text-sm">Infraestrutura que acompanha a exigência de qualidade dos usuários Apple.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10">
                <Info className="w-8 h-8 text-electric mb-4" />
                <p className="text-sm text-muted-foreground italic leading-relaxed">
                  "O público do iPhone busca simplicidade e estabilidade. Oferecer aplicativos oficiais da App Store vinculados ao nosso Painel Pós-Pago é a fórmula perfeita para uma revenda de sucesso."
                </p>
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Comece Sua Revenda no iPhone Agora"
      ctaDescription="Aproveite a melhor interface mobile do mundo com a infraestrutura Pós-Paga mais robusta do Brasil."
    />
  );
}
